import React from 'react';
import { Navigation, Phone, Globe, CheckCircle2, Star } from 'lucide-react';
import { Stethoscope, Pill, Siren, Coffee, Hotel, Bath } from 'lucide-react';

const CAT_ICON = {
  clinic: Stethoscope,
  hospital: Stethoscope,
  pharmacy: Pill,
  emergency: Siren,
  cafe: Coffee,
  hotel: Hotel,
  washroom: Bath,
};

function formatDist(m) {
  if (m == null) return '—';
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

export default function PlaceCard({ place, origin, index }) {
  const Icon = CAT_ICON[place.category] || Stethoscope;
  const url = place.google_maps_uri || `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lon}&origin=${origin?.lat ?? ''},${origin?.lon ?? ''}`;
  const openChip = place.open_now === true
    ? { label: 'Open now', color: '#2ED47A', bg: 'rgba(46,212,122,0.15)' }
    : place.open_now === false
    ? { label: 'Closed', color: '#FF4D4F', bg: 'rgba(255,77,79,0.12)' }
    : place.opening_hours
    ? { label: 'Hours listed', color: '#4A5A50', bg: 'rgba(74,90,80,0.10)' }
    : null;

  return (
    <article className="puvio-card p-0 overflow-hidden puvio-card-interactive" data-testid={`place-card-${index}`}>
      <div className="flex gap-3 p-4">
        {place.photo_url ? (
          <img src={place.photo_url} alt={place.name} className="w-20 h-20 rounded-2xl object-cover shrink-0 bg-[#C4FF3D]/30" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-[#C4FF3D]/30 flex items-center justify-center shrink-0">
            <Icon className="w-8 h-8 text-[#15291C]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold leading-tight line-clamp-2" data-testid={`place-name-${index}`}>{place.name}</h3>
            <span className="text-xs font-medium text-[#15291C] shrink-0">{formatDist(place.distance_m)}</span>
          </div>
          {place.rating != null && (
            <div className="mt-1 flex items-center gap-1 text-xs text-[#4A5A50]">
              <Star className="w-3.5 h-3.5 text-[#FFB020] fill-[#FFB020]" />
              <span className="font-semibold text-[#0F1A13]">{place.rating}</span>
              {place.rating_count != null && <span>({place.rating_count})</span>}
              {place.primary_type && <><span>·</span><span className="truncate">{place.primary_type}</span></>}
            </div>
          )}
          {place.address && <p className="text-xs text-[#4A5A50] mt-1 truncate">{place.address}</p>}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {openChip && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: openChip.bg, color: openChip.color }}>
                <CheckCircle2 className="w-3 h-3" /> {openChip.label}
              </span>
            )}
            {place.wheelchair === 'yes' && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#C4FF3D]/30 text-[#15291C]">♿ Accessible</span>
            )}
            {place.brand && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/5 text-[#4A5A50]">{place.brand}</span>
            )}
          </div>
        </div>
      </div>
      <div className="px-4 pb-4 flex items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 puvio-pill puvio-pill-accent puvio-focus" data-testid={`place-directions-${index}`}>
          <Navigation className="w-3.5 h-3.5" /> Directions
        </a>
        {place.phone && (
          <a href={`tel:${place.phone}`} className="inline-flex items-center gap-1.5 puvio-pill puvio-pill-light puvio-focus" aria-label="Call">
            <Phone className="w-3.5 h-3.5" />
          </a>
        )}
        {place.website && (
          <a href={place.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 puvio-pill puvio-pill-light puvio-focus" aria-label="Website">
            <Globe className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </article>
  );
}
